import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardList, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { todosAPI } from '../../services/api';
import { formatDateOnlyFr } from '../../utils/dateFormat';

const REFRESH_EVENT = 'optic:todo-pending-refresh';

const dismissedStorageKey = (userId) => `optic:todo-alert-dismissed:${userId}`;

function readDismissedIds(userId) {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(dismissedStorageKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function writeDismissedIds(userId, ids) {
  if (!userId) return;
  localStorage.setItem(dismissedStorageKey(userId), JSON.stringify([...ids]));
}

const priorityLabels = {
  critical: 'Critique',
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Faible'
};

/** Fonds, bordure gauche et pastille — alignés sur la page Tâches */
const priorityStyles = {
  critical: {
    card: 'border-l-[6px] border-l-red-600 bg-red-50/95 border-red-200/90 shadow-red-500/10',
    badge: 'bg-gradient-to-r from-red-600 to-red-700 text-white border border-red-900/30 shadow-sm',
    icon: 'text-red-600',
    link: 'text-red-700 hover:text-red-800'
  },
  high: {
    card: 'border-l-[6px] border-l-orange-500 bg-orange-50/95 border-orange-200/90 shadow-orange-500/10',
    badge:
      'bg-gradient-to-r from-orange-500 to-orange-600 text-white border border-orange-800/30 shadow-sm',
    icon: 'text-orange-600',
    link: 'text-orange-700 hover:text-orange-800'
  },
  medium: {
    card: 'border-l-[6px] border-l-amber-500 bg-amber-50/95 border-amber-200/90 shadow-amber-400/10',
    badge:
      'bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-950 border border-amber-600/40 shadow-sm',
    icon: 'text-amber-600',
    link: 'text-amber-800 hover:text-amber-900'
  },
  low: {
    card: 'border-l-[6px] border-l-emerald-500 bg-emerald-50/95 border-emerald-200/90 shadow-emerald-500/10',
    badge:
      'bg-gradient-to-r from-emerald-500 to-green-600 text-white border border-emerald-800/30 shadow-sm',
    icon: 'text-emerald-600',
    link: 'text-emerald-700 hover:text-emerald-800'
  }
};

const TodoAssignmentAlerts = () => {
  const { user, isEmployee, checkInRequired } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(() => readDismissedIds(user?.id));

  useEffect(() => {
    setDismissedIds(readDismissedIds(user?.id));
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    if (isEmployee() && checkInRequired) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await todosAPI.pendingAssignment();
      const pending = Array.isArray(res.data) ? res.data : [];
      setItems(pending);

      const pendingIds = new Set(pending.map((t) => Number(t.id)));
      setDismissedIds((prev) => {
        const next = new Set([...prev].filter((id) => pendingIds.has(id)));
        writeDismissedIds(user.id, next);
        return next;
      });
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isEmployee, checkInRequired]);

  const dismissOne = useCallback((todoId) => {
    if (!user?.id) return;
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(Number(todoId));
      writeDismissedIds(user.id, next);
      return next;
    });
  }, [user?.id]);

  const dismissAll = useCallback(() => {
    if (!user?.id) return;
    const allIds = new Set(items.map((t) => Number(t.id)));
    setDismissedIds((prev) => {
      const next = new Set([...prev, ...allIds]);
      writeDismissedIds(user.id, next);
      return next;
    });
  }, [user?.id, items]);

  const visibleItems = useMemo(
    () => items.filter((t) => !dismissedIds.has(Number(t.id))),
    [items, dismissedIds]
  );

  useEffect(() => {
    load();
  }, [load, location.pathname]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener(REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(REFRESH_EVENT, onRefresh);
  }, [load]);

  if (!user) return null;
  if (loading && visibleItems.length === 0) return null;
  if (visibleItems.length === 0) return null;

  const maxShow = 4;
  const shown = visibleItems.slice(0, maxShow);
  const rest = visibleItems.length - shown.length;

  return (
    <div
      className="fixed z-[90] w-[min(100vw-1.5rem,20rem)] max-h-[calc(100vh-6rem)] overflow-y-auto space-y-2 pr-1
        right-3 top-20 sm:right-5 sm:top-24 pointer-events-none"
      aria-live="polite"
    >
      {visibleItems.length > 1 && (
        <div className="pointer-events-auto flex justify-end">
          <button
            type="button"
            onClick={dismissAll}
            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white/95 px-2 py-1 text-[11px] font-semibold text-neutral-600 shadow hover:bg-neutral-50"
          >
            <X className="w-3 h-3" aria-hidden />
            Tout fermer
          </button>
        </div>
      )}
      {shown.map((t) => {
        const ps = priorityStyles[t.priority] || priorityStyles.medium;
        const prioLabel = priorityLabels[t.priority] || priorityLabels.medium;
        return (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-lg border shadow-lg backdrop-blur-sm ${ps.card}`}
          >
            <div className="flex items-start gap-2 p-3">
              <ClipboardList
                className={`w-5 h-5 shrink-0 mt-0.5 ${ps.icon}`}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 gap-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
                    Nouvelle tâche assignée
                  </p>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${ps.badge}`}
                  >
                    {prioLabel}
                  </span>
                </div>
                <p className="text-sm font-medium text-neutral-900 line-clamp-2 mt-1">{t.title}</p>
                {t.due_date && (
                  <p className="text-xs text-neutral-600 mt-1">
                    Échéance&nbsp;: {formatDateOnlyFr(t.due_date)}
                  </p>
                )}
                <Link
                  to="/todos"
                  className={`inline-block mt-2 text-xs font-semibold ${ps.link}`}
                >
                  Ouvrir les tâches
                </Link>
              </div>
              <button
                type="button"
                onClick={() => dismissOne(t.id)}
                className="shrink-0 rounded-md p-1 text-neutral-400 hover:bg-black/5 hover:text-neutral-700"
                aria-label="Fermer la notification"
                title="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
      {rest > 0 && (
        <div className="pointer-events-auto text-center">
          <Link
            to="/todos"
            className="text-xs font-semibold text-primary-600 hover:text-primary-700 bg-white/90 px-2 py-1 rounded-md shadow border border-neutral-200"
          >
            +{rest} autre{rest > 1 ? 's' : ''} tâche{rest > 1 ? 's' : ''}
          </Link>
        </div>
      )}
    </div>
  );
};

export default TodoAssignmentAlerts;
export { REFRESH_EVENT };
